import { useState, useCallback, useEffect } from 'react'
import { usePersistedState } from './usePersistedState'

export type NotifPermission = 'default' | 'granted' | 'denied' | 'unsupported'

export interface UseDesktopNotifResult {
  permission: NotifPermission
  enabled: boolean
  requestPermission: () => Promise<void>
  send: (title: string, opts?: NotificationOptions) => void
  setEnabled: (v: boolean) => void
}

export function useDesktopNotif(): UseDesktopNotifResult {
  const [permission, setPermission] = useState<NotifPermission>(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
    return Notification.permission as NotifPermission
  })
  const [enabled, setEnabled] = usePersistedState<boolean>('cc-notif-enabled', true)

  // Sync permission state si elle change depuis l'extérieur
  useEffect(() => {
    if (!('Notification' in window)) return
    setPermission(Notification.permission as NotifPermission)
  }, [])

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) return
    if (Notification.permission === 'granted') {
      setPermission('granted')
      return
    }
    const result = await Notification.requestPermission()
    setPermission(result as NotifPermission)
  }, [])

  const send = useCallback((title: string, opts?: NotificationOptions) => {
    if (permission !== 'granted' || !enabled) return
    try {
      new Notification(title, {
        icon: '/logo-kaytech.jpg',
        badge: '/logo-kaytech.jpg',
        ...opts,
      })
    } catch {
      // Silencieux si le navigateur bloque
    }
  }, [permission, enabled])

  return { permission, enabled, requestPermission, send, setEnabled }
}
